import { CONFIG } from '@arrived/config';

const ACCOUNTS_ROUTES = {
  accountDetails: '/accounts/:id',
  accountEdit: '/accounts/:id/edit',
  accountSuitability: '/accounts/suitability-questionnaire',
  base: '/accounts',
  createAccount: '/accounts/create',
  newAccountSuccess: '/accounts/:id/new/success',
  addBank: '/accounts/bank/link',
  newBank: '/accounts/:id/bank/new',
  verifyBank: '/accounts/bank/verify',
  newBankOnboarding: '/accounts/:id/bank/new/onboarding',
};

const TEST_ROUTES = {
  base: '/admin/test',
  buttons: '/admin/test/buttons',
};

const ADMIN_USER_BASE = '/admin/user';
const ADMIN_OFFERING_BASE = '/admin/offerings';
const ADMIN_PROPERTIES_BASE = '/admin/properties';
const ADMIN_TRANSACTIONS_BASE = '/admin/transactions';

const ADMIN_ROUTES = {
  base: '/admin',
  dev: '/admin/dev',
  markets: '/admin/markets',
  promotions: '/admin/promotions',
  OFFERINGS: {
    base: ADMIN_OFFERING_BASE,
    details: `${ADMIN_OFFERING_BASE}/:id`,
  },
  profile: '/admin/profile',
  PROPERTIES: {
    base: ADMIN_PROPERTIES_BASE,
    details: `${ADMIN_PROPERTIES_BASE}/:id`,
    features: `${ADMIN_PROPERTIES_BASE}/features`,
  },
  RESERVATIONS: {
    base: '/admin/transactions/reservations',
    daily: '/admin/transactions/reservations/daily',
  },
  TRANSACTIONS: {
    base: ADMIN_TRANSACTIONS_BASE,
    create: `${ADMIN_TRANSACTIONS_BASE}/create`,
    // reservations: `${ADMIN_TRANSACTIONS_BASE}/reservations`,
    // trades: `${ADMIN_TRANSACTIONS_BASE}/trades`,
  },
  USERS: {
    base: ADMIN_USER_BASE,
    details: `${ADMIN_USER_BASE}/:id`,
  },
  test: TEST_ROUTES,
};

const PORTFOLIO_ROUTES = {
  base: '/portfolio',
};

const DOCUMENT_ROUTES = {
  base: '/documents',
  documentREITLetter: '/documents/reit-demand-letter',
};

const PROFILE_ROUTES = {
  base: '/profile',
  profileSecurity: '/profile/security',
  profileBasicInfo: '/profile/basic-info',
  profileAbout: '/profile/about',
  profileAccount: '/profile/account',
  profileName: '/profile/account/name',
  profilePhoneNumber: '/profile/account/phone-number',
  profileBeneficiaries: '/profile/account/beneficiaries',
  profileBirthDate: '/profile/account/birth-date',
  profileTaxId: '/profile/account/tax-id',
  profileEntityInfo: '/profile/account/entity-info',
  profilePrimaryResidenceAddress: '/profile/account/primary-residence-address',
  profileMailingAddress: '/profile/account/mailing-address',
  profilePrimaryResidence: '/profile/account/primary-residence',
  profileDocuments: '/profile/documents',
  profileTradeDocuments: '/profile/documents/trade',
  profileTaxDocuments: '/profile/documents/tax',
  profileOtherDocuments: '/profile/documents/other',
  profileUploadDocuments: '/profile/documents/upload',
  profileUploadIdentityDocuments: '/profile/documents/identity/upload',
  profileTransactions: '/profile/transactions',
  profileScheduleMeeting: '/profile/meeting',
  profileScheduleWebinar: '/profile/webinar',
};

const REFERRAL_ROUTES = {
  base: '/refer',
  earn: '/earn',
  redeem: '/profile/redeem-code',
};

const TRADES_ROUTES = {
  base: '/trades',
  tradeDetails: '/trades/:tradeId',
  tradeDocuments: '/trades/:tradeId/documents',
  tradeDocumentsSign: '/trades/:tradeId/documents/sign',
  transactionSuccess: '/trades/:tradeId/success',
  transactionMissingData: '/trades/:tradeId/user/update',
};

const INLINE_PROPERTY_TRADE_ROOT = '/properties/:idOrShortName';

const PROPERTIES_ROUTES = {
  base: '/properties',
  propertyDetails: INLINE_PROPERTY_TRADE_ROOT,
  propertyTransactShares: `${INLINE_PROPERTY_TRADE_ROOT}/get-shares`,
  TRADES: {
    base: `${INLINE_PROPERTY_TRADE_ROOT}${TRADES_ROUTES.base}`,
    tradeDetails: `${INLINE_PROPERTY_TRADE_ROOT}${TRADES_ROUTES.tradeDetails}`,
    tradeDocumentsSign: `${INLINE_PROPERTY_TRADE_ROOT}${TRADES_ROUTES.tradeDocumentsSign}`,
    transactionMissingData: `${INLINE_PROPERTY_TRADE_ROOT}${TRADES_ROUTES.transactionMissingData}`,
    transactionSuccess: `${INLINE_PROPERTY_TRADE_ROOT}${TRADES_ROUTES.transactionSuccess}`,
  },
  generateFilterTags: (tags?: string) => (tags ? `?tags=${tags}` : undefined),
};

const PUBLIC_ROUTES = {
  base: '/',
  properties: {
    base: '/properties',
    propertyDetails: {
      base: '/properties/[shortName]',
    },
  },
  about: '/about',
  careers: '/careers',
  returns: '/returns',
  vacationRentals: '/vacation-rentals',
  offeringCircular: '/circulars',
  register: '/register',
};

const STATIC_ROUTES = {
  learning: `${CONFIG.learnBlogUrl}/`,
  blog: `${CONFIG.learnBlogUrl}/category/investing-on-arrived/`,
  howItWorks: `${CONFIG.learnBlogUrl}/how-it-works/`,
  whyInvest: `${CONFIG.learnBlogUrl}/why-invest-in-arrived-homes/`,
  glossary: `${CONFIG.learnBlogUrl}/glossary/`,
  sellYourHome: `${CONFIG.learnBlogUrl}/sell-us-your-home/`,
  partnerWithArrivedSurvey: 'https://www.surveymonkey.com/r/collabwitharrived',
  sellYourHomeSurvey: 'https://www.surveymonkey.com/r/ZR38WMS',
  realEstateInvestingGuide: `${CONFIG.learnBlogUrl}/real-estate-investing-guide/`,
  help: CONFIG.helpUrl,
  careers: CONFIG.careersUrl,
  base: CONFIG.publicAppRoot,
  webinar: `${CONFIG.learnBlogUrl}/arrived-webinars/`,
  cityPages: CONFIG.cityPageUrl,
  stakeHolderCommitments: 'https://arrived.com/blog/arrived-commitments/',

  checkbookIRAVacationRentalFAQ: `${CONFIG.helpUrl}articles/6524055-can-i-invest-in-a-vacation-rentals-with-my-checkbook-ira`,
  stakeholderCommitments: `${CONFIG.learnBlogUrl}/arrived-commitments/`,
};

export const ROUTES = {
  verifyEmail: '/verify-email',
  aboutUs: '/about',
  accounts: ACCOUNTS_ROUTES,
  admin: ADMIN_ROUTES,
  authCallback: '/auth/callback',
  base: '/',
  careers: '/careers',
  communicationsDisclaimer: '/communications-disclaimer',
  liveHelp: '/live-help',
  logIn: '/login',
  logOut: '/logout',
  logOutCleanUp: '/logout/callback',
  marketing: '/marketing',
  portfolio: PORTFOLIO_ROUTES,
  privacyPolicy: '/privacy-policy',
  privacy: '/privacy',
  profile: PROFILE_ROUTES,
  properties: PROPERTIES_ROUTES,
  referral: REFERRAL_ROUTES,
  returns: '/returns',
  signUp: '/signup',
  register: '/register',
  userSignUp: '/user/signup',
  termsOfService: '/terms-of-service',
  tos: '/tos',
  trades: TRADES_ROUTES,
  static: STATIC_ROUTES,
  fourOhThree: '/403',
  fourOhFour: '/404',
  fiveHundred: '/500',
  maintenance: '/maintenance',
  documents: DOCUMENT_ROUTES,
  public: PUBLIC_ROUTES,
};

// accounts/documents/trades
export interface MatchIdParams {
  id: string;
}

// Typically, used for properties/invest
export interface MatchIdOrShortNameParams {
  idOrShortName: string;
}
